import { z } from 'zod'
import { nerEntitySchema, tagSchema, topicDraftSchema } from './topic'

/**
 * Derek Evaluation (V1 demo) — an agentic review of a topic that returns up to five concrete,
 * ACCEPTABLE suggestions to improve its ontology (tags), query scope, and reasoning prompt.
 *
 * Each suggestion carries a machine-applicable `change` so the overlay can apply it to the unsaved
 * draft on accept. The change kinds are exactly the draft mutations that are safe to auto-apply —
 * news queries, a news exclusion, a topic-level tag, or the reasoning prompt. Advice about other
 * sources rides in the rationale, not as an auto-apply. Kept in its own schema/route/lib/component
 * family because the feature is expected to be short-lived and must delete clean.
 */
export const derekRequestSchema = z.object({
  draft: topicDraftSchema,
  /** The sub-account's tag pool, so Derek can resolve the draft's tag ids to entity/value and see
   *  what ontology already exists before suggesting new tags. */
  tags: z.array(tagSchema),
})
export type DerekRequest = z.infer<typeof derekRequestSchema>

/**
 * A single applicable change, discriminated by `kind`:
 * - `add_query` — add a positive NEWS query.
 * - `edit_query` — rewrite an existing NEWS query (by its draft id).
 * - `add_exclusion` — add a NEWS exclusion query.
 * - `add_tag` — add a topic-level tag (one NER entity + value).
 * - `set_prompt` — set the reasoning-filter prompt (and enable it).
 */
export const derekChangeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('add_query'), expression: z.string().min(1).max(2000) }),
  z.object({
    kind: z.literal('edit_query'),
    queryId: z.string().min(1),
    expression: z.string().min(1).max(2000),
  }),
  z.object({ kind: z.literal('add_exclusion'), expression: z.string().min(1).max(2000) }),
  z.object({
    kind: z.literal('add_tag'),
    entity: nerEntitySchema,
    value: z.string().min(1).max(120),
  }),
  z.object({ kind: z.literal('set_prompt'), prompt: z.string().min(1).max(4000) }),
])
export type DerekChange = z.infer<typeof derekChangeSchema>

/** The axis a suggestion improves — drives the card's label/icon, not the apply. */
export const derekCategorySchema = z.enum(['tags', 'scope', 'prompt', 'coverage'])
export type DerekCategory = z.infer<typeof derekCategorySchema>

export const derekSuggestionSchema = z.object({
  id: z.string(),
  category: derekCategorySchema,
  /** A short imperative headline, e.g. "Exclude sports coverage of the name". */
  title: z.string().min(1).max(160),
  /** Why — grounded in the topic's intent, ontology, and scope. One or two sentences. */
  rationale: z.string().min(1).max(1000),
  change: derekChangeSchema,
})
export type DerekSuggestion = z.infer<typeof derekSuggestionSchema>

export const derekResponseSchema = z.object({
  /** Derek's read of what the topic is about — shown above the cards. */
  interpretation: z.string().max(2000),
  suggestions: z.array(derekSuggestionSchema).max(5),
})
export type DerekResponse = z.infer<typeof derekResponseSchema>
